import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanyInvoice } from 'src/app/core/models/sales/invoice.interface';
import {
  InvoiceStatus,
  canMarkInvoicePaid,
  getInvoiceStatusLabelKey,
  normalizeInvoiceStatus,
} from 'src/app/core/models/sales/invoice-status.type';
import { InvoiceService } from 'src/app/core/services/invoice.service';
import { InvoiceModalComponent } from '../../../invoices/invoice-modal/invoice-modal.component';

interface InvoiceFinanceRow {
  id: string;
  number: string;
  invoiceDate: string;
  status: InvoiceStatus;
  statusLabelKey: string;
  statusBadgeClass: string;
  // Precomputed status-tinted classes for the leading invoice-document icon block. Bound from
  // the template via direct property access — no method calls (AGENTS.md rule 9 / Angular
  // template rule). Extend `INVOICE_STATUS_ICON_CLASSES` when new InvoiceStatus values land.
  iconBgClass: string;
  iconTextClass: string;
  total: number;
  canMarkPaid: boolean;
  // Precomputed per-row mutation flags so the template never invokes component methods
  // (AGENTS.md rule 9 / Angular template rule). `markPaid` flips these via patchRowFlags.
  isMutating: boolean;
  errorKey: string | null;
}

interface InvoiceFinanceSums {
  invoicedExcludingTax: number;
  invoicedTax: number;
  invoicedIncludingTax: number;
  paidIncludingTax: number;
  invoiceCount: number;
}

// Aggregate order-level payment health derived from invoice statuses + order grand total.
// Mirrors the FINANCE tab "Статус оплати" block in erp-business-Apr/order-overview.ts:731-755:
// `paid` when every invoice is Paid AND the paid amount covers the order total; `partial`
// when at least one invoice is Paid; `unpaid` otherwise (incl. no invoices yet).
export type InvoicePaymentStatus = 'paid' | 'partial' | 'unpaid';

// Precomputed view-model so the template never invokes component methods (AGENTS.md
// rule 9 / Angular template rule). The check vs. info icon and the colour theming are
// resolved here and bound directly from the template.
export interface InvoicePaymentStatusVm {
  status: InvoicePaymentStatus;
  labelKey: string;
  iconBgClass: string;
  iconTextClass: string;
  // `check` renders the ✓ glyph for fully-paid; `info` renders the ⓘ glyph used for the
  // partial + unpaid states (mirrors the SVG fork at order-overview.ts:742-743).
  iconVariant: 'check' | 'info';
  paidAmount: number;
  totalAmount: number;
}

const PAYMENT_STATUS_LABEL_KEYS: Readonly<Record<InvoicePaymentStatus, string>> = {
  paid: 'COMPONENTS.SALES_ORDERS.FINANCE.PAYMENT_STATUS.FULLY_PAID',
  partial: 'COMPONENTS.SALES_ORDERS.FINANCE.PAYMENT_STATUS.PARTIALLY_PAID',
  unpaid: 'COMPONENTS.SALES_ORDERS.FINANCE.PAYMENT_STATUS.NOT_PAID',
};

const PAYMENT_STATUS_ICON_CLASSES: Readonly<
  Record<InvoicePaymentStatus, { bg: string; text: string; variant: 'check' | 'info' }>
> = {
  paid: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    variant: 'check',
  },
  partial: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    variant: 'info',
  },
  unpaid: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
    variant: 'info',
  },
};

// Tailwind status pill classes precomputed on the row so the template never invokes
// component methods for visual state (AGENTS.md rule 9 / Angular template rule).
const INVOICE_STATUS_BADGE_CLASSES: Readonly<Record<InvoiceStatus, string>> = {
  Open: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  Paid: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
};

// Status-tinted icon-block surface classes mirrored from erp-business-Apr/order-overview.ts
// (FINANCE tab reference, lines 703–711). Open invoices read as a red/danger document and
// Paid invoices read as a green/success document. The backend currently emits Open + Paid
// only; if `Partial` / `Overdue` ever land, add them here.
const INVOICE_STATUS_ICON_CLASSES: Readonly<
  Record<InvoiceStatus, { bg: string; text: string }>
> = {
  Open: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
  },
  Paid: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
};

@Component({
  selector: 'app-sales-order-finance-tab',
  standalone: true,
  imports: [
    CommonModule,
    CoreModule,
    TranslateModule,
    InvoiceModalComponent,
  ],
  templateUrl: './sales-order-finance-tab.component.html',
  styleUrl: './sales-order-finance-tab.component.scss',
})
export class SalesOrderFinanceTabComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input() public unitsOfMeasure: UnitOfMeasureLookup[] = [];
  @Input() public disabled = false;
  // Resolved human-readable customer name forwarded from the parent overview to the
  // create-invoice modal. The modal falls back to the order's customerNumber when empty.
  @Input() public customerName = '';
  // Bumped by the parent overview whenever the underlying sales order is re-fetched (e.g.
  // after a quick-action invoice creation on the Overview tab). The change handler clears
  // the dedupe key so `loadInvoicesIfNeeded` re-fires, keeping the list in sync with totals.
  @Input() public refreshToken = 0;

  @Output() public readonly invoiceCreated = new EventEmitter<void>();
  // The "+ NEW" button bubbles to the parent overview so a single create-invoice modal
  // instance mounted at the page-content level handles every entry point (Overview quick
  // action AND in-tab CTA). Eliminates the dual-mount visual drift the previous design
  // had between the Overview and Finance entry points.
  @Output() public readonly createInvoiceRequested = new EventEmitter<void>();

  public readonly invoiceRows$ = new BehaviorSubject<InvoiceFinanceRow[]>([]);
  public readonly invoiceSums$ = new BehaviorSubject<InvoiceFinanceSums>(this.emptySums());
  public readonly paymentStatus$ = new BehaviorSubject<InvoicePaymentStatusVm | null>(null);

  public isLoading = false;
  public hasLoadError = false;
  public canCreateInvoice = false;

  // Detail-modal state. The standalone /invoices/:id route is still wired in
  // crm-routing.module.ts so deep links keep working.
  public isDetailModalOpen = false;
  public selectedInvoiceId: string | null = null;

  // Per-row Mark-paid mutation guards keyed by invoice id so concurrent ops never collide.
  // The Set is the source of truth for the in-flight guard; InvoiceFinanceRow.isMutating
  // mirrors it for template binding so the template never invokes component methods.
  public readonly mutatingRowIds = new Set<string>();

  private readonly unsubscribe$ = new Subject<void>();
  private lastLoadedOrderId = '';
  private readonly invoiceService = inject(InvoiceService);

  // Map structured backend error codes (InvoicesController) to per-row i18n keys.
  // Keep narrow: the only mark-paid failure modes the user can recover from are
  // "already paid" / "wrong status"; everything else falls back to MARK_PAID_FAILED.
  private readonly markPaidErrorMap: Readonly<Record<string, string>> = {
    INVOICE_INVALID_STATUS: 'COMPONENTS.INVOICES.ERRORS.INVOICE_INVALID_STATUS',
    INVOICE_TERMINAL: 'COMPONENTS.INVOICES.ERRORS.INVOICE_TERMINAL',
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['salesOrder'] || changes['companyId']) {
      this.recomputeFlags();
      this.loadInvoicesIfNeeded();
    }
    // Parent-driven invalidation: bumping `refreshToken` forces a refetch even when the
    // sales order identity has not changed (e.g. a quick-action invoice creation that re-
    // fetches the same order). Skip the firstChange pass so we do not double-load on mount.
    if (
      changes['refreshToken'] &&
      !changes['refreshToken'].firstChange &&
      changes['refreshToken'].currentValue !== changes['refreshToken'].previousValue
    ) {
      this.lastLoadedOrderId = '';
      this.loadInvoicesIfNeeded();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public retry(): void {
    this.lastLoadedOrderId = '';
    this.loadInvoicesIfNeeded();
  }

  /**
   * In-tab "+ NEW" button handler — bubbles up to the parent overview, which owns the
   * single create-invoice modal mount. The parent re-fetches the order on success and
   * bumps `refreshToken` so this tab's invoice list reloads via `ngOnChanges`.
   */
  public requestCreateInvoice(): void {
    if (!this.canCreateInvoice || this.disabled) {
      return;
    }
    this.createInvoiceRequested.emit();
  }

  public openInvoice(invoiceId: string): void {
    if (!this.companyId || !this.salesOrder) {
      return;
    }
    this.selectedInvoiceId = invoiceId;
    this.isDetailModalOpen = true;
  }

  public closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedInvoiceId = null;
  }

  /**
   * Modal emitted `paid` — refetch the invoice list so the row badge flips to Paid + sums update,
   * and forward to the parent so other tabs (Lines, Overview totals) refresh.
   */
  public onInvoicePaid(): void {
    this.lastLoadedOrderId = '';
    this.loadInvoicesIfNeeded();
    this.invoiceCreated.emit();
  }

  /**
   * Modal emitted `deleted` — same refresh path as paid: list reload + parent emit.
   */
  public onInvoiceDeleted(): void {
    this.closeDetailModal();
    this.lastLoadedOrderId = '';
    this.loadInvoicesIfNeeded();
    this.invoiceCreated.emit();
  }

  /**
   * Per-row Open -> Paid transition. Forward-only on the backend; FE only exposes the action
   * when `row.status === 'Open'` and the parent-level `disabled` flag is false (terminal order).
   */
  public markPaid(row: InvoiceFinanceRow): void {
    if (this.disabled || !this.companyId) {
      return;
    }
    if (!row.canMarkPaid || this.mutatingRowIds.has(row.id)) {
      return;
    }

    this.mutatingRowIds.add(row.id);
    // Flip the precomputed flags so the template reads VM fields instead of methods.
    this.patchRowFlags(row.id, { isMutating: true, errorKey: null });

    this.invoiceService
      .markCompanyInvoicePaid(this.companyId, row.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          // Refetch so totals + sums reflect the new paid amount in one source of truth.
          // The reload rebuilds rows fresh, which naturally clears isMutating/errorKey.
          this.lastLoadedOrderId = '';
          this.loadInvoicesIfNeeded();
          this.invoiceCreated.emit();
        }),
        catchError((error: HttpErrorResponse) => {
          this.patchRowFlags(row.id, {
            isMutating: false,
            errorKey: this.mapMarkPaidErrorKey(error),
          });
          return EMPTY;
        }),
        finalize(() => {
          this.mutatingRowIds.delete(row.id);
        }),
      )
      .subscribe();
  }

  public isMutating(rowId: string): boolean {
    return this.mutatingRowIds.has(rowId);
  }

  /** Test helper that reads the precomputed VM error key. Not used from the template. */
  public rowErrorKey(rowId: string): string {
    return this.invoiceRows$.getValue().find((row) => row.id === rowId)?.errorKey ?? '';
  }

  /**
   * Patch only the per-row mutation flags without re-fetching invoices. Used to flip
   * `isMutating`/`errorKey` from the markPaid pipeline so the template binds to VM fields.
   */
  private patchRowFlags(
    rowId: string,
    flags: { isMutating: boolean; errorKey: string | null },
  ): void {
    const current = this.invoiceRows$.getValue();
    const replaced = current.map((row) =>
      row.id === rowId ? { ...row, isMutating: flags.isMutating, errorKey: flags.errorKey } : row,
    );
    this.invoiceRows$.next(replaced);
  }

  private mapMarkPaidErrorKey(error: HttpErrorResponse): string {
    const code = (error.error as { code?: string } | null)?.code;
    if (code && this.markPaidErrorMap[code]) {
      return this.markPaidErrorMap[code];
    }
    return 'COMPONENTS.SALES_ORDERS.FINANCE.MARK_PAID_FAILED';
  }

  private recomputeFlags(): void {
    const status = this.salesOrder?.status ?? null;
    // Cancelled is the only terminal state that blocks invoice creation; a missing
    // status keeps the CTA disabled until the parent has loaded the order.
    this.canCreateInvoice = status !== null && status !== 'Cancelled';
  }

  private loadInvoicesIfNeeded(): void {
    const order = this.salesOrder;
    if (!order || !this.companyId) {
      this.invoiceRows$.next([]);
      this.invoiceSums$.next(this.emptySums());
      this.paymentStatus$.next(null);
      return;
    }

    if (order.id === this.lastLoadedOrderId) {
      return;
    }

    this.lastLoadedOrderId = order.id;
    this.isLoading = true;
    this.hasLoadError = false;

    this.invoiceService
      .getInvoices(this.companyId, order.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoices) => {
          const sums = this.computeSums(invoices);
          this.invoiceRows$.next(invoices.map((invoice) => this.mapRow(invoice)));
          this.invoiceSums$.next(sums);
          this.paymentStatus$.next(this.computePaymentStatus(invoices, sums));
        }),
        catchError(() => {
          this.hasLoadError = true;
          this.invoiceRows$.next([]);
          this.invoiceSums$.next(this.emptySums());
          this.paymentStatus$.next(null);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private mapRow(invoice: CompanyInvoice): InvoiceFinanceRow {
    const normalized = normalizeInvoiceStatus(invoice.status);
    const iconClasses = INVOICE_STATUS_ICON_CLASSES[normalized];
    return {
      id: invoice.id,
      number: invoice.number,
      invoiceDate: invoice.invoiceDate,
      status: normalized,
      statusLabelKey: getInvoiceStatusLabelKey(normalized),
      statusBadgeClass: INVOICE_STATUS_BADGE_CLASSES[normalized],
      iconBgClass: iconClasses.bg,
      iconTextClass: iconClasses.text,
      total: invoice.total,
      // Mark-paid eligibility comes from canMarkInvoicePaid (shared with the modal footer)
      // so the row gate and modal footer stay in lockstep with backend MarkPaidAsync.
      canMarkPaid: canMarkInvoicePaid(normalized),
      isMutating: false,
      errorKey: null,
    };
  }

  private computeSums(invoices: CompanyInvoice[]): InvoiceFinanceSums {
    return invoices.reduce<InvoiceFinanceSums>(
      (acc, invoice) => {
        const normalized = normalizeInvoiceStatus(invoice.status);
        acc.invoicedExcludingTax += invoice.subtotal ?? 0;
        acc.invoicedTax += invoice.taxTotal ?? 0;
        acc.invoicedIncludingTax += invoice.total ?? 0;
        if (normalized === 'Paid') {
          acc.paidIncludingTax += invoice.total ?? 0;
        }
        acc.invoiceCount += 1;
        return acc;
      },
      this.emptySums(),
    );
  }

  /**
   * Build the order-level payment-status VM from the just-fetched invoice list + precomputed sums.
   *
   * Rules (mirrors erp-business-Apr/order-overview.ts:1043-1050):
   *  - no invoices → `unpaid` (nothing has been billed yet)
   *  - every invoice is Paid → `paid`
   *  - at least one invoice is Paid → `partial`
   *  - otherwise → `unpaid`
   *
   * `totalAmount` is the order grand total (incl. tax) so the template can render
   * "Paid: X of Y" without recomputing anywhere — keeps the template method-call-free.
   */
  private computePaymentStatus(
    invoices: CompanyInvoice[],
    sums: InvoiceFinanceSums,
  ): InvoicePaymentStatusVm {
    const orderTotal = this.salesOrder?.totals.amountIncludingTax ?? 0;
    let status: InvoicePaymentStatus;
    if (invoices.length === 0) {
      status = 'unpaid';
    } else {
      const normalizedStatuses = invoices.map((invoice) => normalizeInvoiceStatus(invoice.status));
      if (normalizedStatuses.every((value) => value === 'Paid')) {
        status = 'paid';
      } else if (normalizedStatuses.some((value) => value === 'Paid')) {
        status = 'partial';
      } else {
        status = 'unpaid';
      }
    }
    const iconClasses = PAYMENT_STATUS_ICON_CLASSES[status];
    return {
      status,
      labelKey: PAYMENT_STATUS_LABEL_KEYS[status],
      iconBgClass: iconClasses.bg,
      iconTextClass: iconClasses.text,
      iconVariant: iconClasses.variant,
      paidAmount: sums.paidIncludingTax,
      totalAmount: orderTotal,
    };
  }

  private emptySums(): InvoiceFinanceSums {
    return {
      invoicedExcludingTax: 0,
      invoicedTax: 0,
      invoicedIncludingTax: 0,
      paidIncludingTax: 0,
      invoiceCount: 0,
    };
  }
}
